// src/Pages/AssetCollection.js
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, API_BASE } from '../api';
import emptyArt from '../assets/emptyart.jpg';
import ReleaseAssets from '../Components/ReleaseAssets';
import { ReactComponent as ViewIcon } from '../assets/icons/view.svg';
import { ReactComponent as DownloadIcon } from '../assets/icons/download.svg';
import { ReactComponent as CheckCircleIcon } from '../assets/icons/checkcircle.svg';
import { ReactComponent as LockedIcon } from '../assets/icons/locked.svg';
import { ReactComponent as UnlockedIcon } from '../assets/icons/unlocked.svg';

const _iconCss = (
  <style>{`
    /* Make the circle green, no outline */
    .ck-check circle, .ck-check ellipse {
      fill: #28a745 !important;
      stroke: none !important;
    }

    /* Make the check mark white stroke, no fill */
    .ck-check path, .ck-check polyline, .ck-check line {
      fill: none !important;
      stroke: #ffffff !important;
      stroke-width: 2 !important;
      stroke-linecap: round !important;
      stroke-linejoin: round !important;
    }
  `}</style>
);

/* ---------- UI helpers ---------- */
const Chip = ({ active, onClick, children, colorActive = '#28a745', disabled }) => (
  <div
    onClick={disabled ? undefined : onClick}
    style={{
      padding: '6px 10px',
      borderRadius: 6,
      cursor: disabled ? 'default' : 'pointer',
      userSelect: 'none',
      fontWeight: 600,
      background: active ? colorActive : '#2c2c2c',
      color: '#fff',
      lineHeight: 1,
      opacity: disabled ? 0.85 : 1,
    }}
  >
    {children}
  </div>
);

const Row = ({ label, children, compact }) => (
  <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: compact ? 8 : 12 }}>
    <label style={{ width: 160, color: '#bbb' }}>{label}</label>
    <div>{children}</div>
  </div>
);

const Section = ({ title, children }) => (
  <div
    style={{
      background: '#1e1e1e',
      padding: 16,
      borderRadius: 10,
      marginBottom: 16,
      border: 'none',
    }}
  >
    <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <h3 style={{ margin: 0 }}>{title}</h3>
    </div>
    {children}
  </div>
);

const input = {
  base: {
    width: 300,
    padding: 8,
    background: '#2a2a2a',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    fontFamily: 'inherit',
  },
  sm: {
    width: 250,
    padding: 6,
    background: '#2a2a2a',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    fontFamily: 'inherit',
  },
  wide: {
    width: 560,
    maxWidth: '100%',
    padding: 8,
    background: '#2a2a2a',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    fontFamily: 'inherit',
  },
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d
      .toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' })
      .replaceAll('/', '.');
  } catch {
    return '—';
  }
};

const iconGreen = {
  width: 16,
  height: 16,
  fill: '#28a745',
};

/* --- Required-fields checker for Save & Submit --- */
function isFormComplete(st, projectType) {
  // 1) Title
  if (!String(st.title || '').trim()) return false;

  // 2) Tracklist # only for Album/EP
  const needsTracklist = projectType === 'Album' || projectType === 'EP';
  if (needsTracklist) {
    if (st.tracklistNumber === '' || st.tracklistNumber == null || isNaN(Number(st.tracklistNumber))) {
      return false;
    }
  }

  // 3) Booleans must be explicitly set
  if (st.hasVocals !== true && st.hasVocals !== false) return false;
  if (st.isExplicit !== true && st.isExplicit !== false) return false;

  // 4) Credits
  if (!String(st.producer || '').trim()) return false;
  if (!String(st.recordingCountry || '').trim()) return false;

  // 5) At least one writer with a name
  const writers = Array.isArray(st.writers) ? st.writers : [];
  const hasWriter = writers.some(w => String(w?.legalName || '').trim());
  if (!hasWriter) return false;

  // 6) Required uploads
  const u = st.uploads || {};
  if (!u.lyricsPdf) return false;
  if (!u.losslessMaster) return false;
  if (!u.mp3Master) return false;

  return true;
}

export default function AssetCollection() {
  const { id } = useParams(); // projectId
  const navigate = useNavigate();

    // detect guest mode (?guest=1 or ?access=<token>)
  const params = new URLSearchParams(window.location.search);
  const guest = params.get('guest') === '1' || params.has('access');

  /* loading */
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  /* data */
  const [project, setProject]   = useState(null);
  const [releases, setReleases] = useState([]);
  const [allArtists, setAllArtists] = useState([]);

  /* project (identifiers only editable here) */
  const [projTitle, setProjTitle]             = useState('');
  const [projType, setProjType]               = useState('Album');
  const [projReleaseDate, setProjReleaseDate] = useState('');
  const [isTitleEditing, setIsTitleEditing] = useState(false);
const [titleDraft, setTitleDraft]         = useState('');
const [titleSaving, setTitleSaving]       = useState(false);
  const [projCatalog, setProjCatalog]         = useState(''); // projectCatalogueNumber
  const [projUpc, setProjUpc]                 = useState(''); // projectUpc
  const [openProjIdents, setOpenProjIdents]   = useState(false);
  const [identsEditing, setIdentsEditing]     = useState(false); // project identifiers editing lock

  /* per-release state */
  const [releaseState, setReleaseState]   = useState({}); // relId -> object
  // per-release submitting flag for the "Save & Submit" button
const [submitting, setSubmitting] = useState({}); // { [relId]: true|false }
  const [openDetails, setOpenDetails]     = useState({}); // relId -> bool (asset form)
  const [openIdents, setOpenIdents]       = useState({}); // relId -> bool (identifiers)
  const [relIdentsEditing, setRelIdentsEditing] = useState({}); // relId -> bool (release identifiers edit)
  const [assigning, setAssigning] = useState({}); // { [`${relId}_field`]: boolean }
  // Per-release "Add to District" UI states
const [relDistrictAppending, setRelDistrictAppending] = useState({}); // { [relId]: boolean }
const [relDistrictAdded, setRelDistrictAdded] = useState({});         // { [relId]: boolean }
// Per-release "Add to Killphonic" UI states
const [relKillphonicAppending, setRelKillphonicAppending] = useState({}); // { [relId]: boolean }
const [relKillphonicAdded, setRelKillphonicAdded] = useState({});         // { [relId]: boolean }
// Per-release "Add to Creators" UI state
const [relCreatorsAppending, setRelCreatorsAppending] = useState({});     // { [relId]: boolean }
const [relCreatorsAdded, setRelCreatorsAdded] = useState({});             // { [relId]: boolean }
  const setRelEditing = (relId, val) =>
    setRelIdentsEditing(prev => ({ ...prev, [relId]: !!val }));

// Project-level Sheet status (UI only)
const [projectAddedToSheet, setProjectAddedToSheet] = useState(false);
// Reset project-level button when switching to another project
useEffect(() => {
  setProjectAddedToSheet(false);
}, [id]);

// Track loading state for disabling the "Add All to Sheet" button
const [projectAppending, setProjectAppending] = useState(false);

// District button states
const [projectDistrictAdded, setProjectDistrictAdded] = useState(false);
const [projectDistrictAppending, setProjectDistrictAppending] = useState(false);

// Killphonic button states
const [projectKillphonicAdded, setProjectKillphonicAdded] = useState(false);
const [projectKillphonicAppending, setProjectKillphonicAppending] = useState(false);

  /* load */
  useEffect(() => {
    (async () => {
      try {
        setLoading(true); setError('');

        // Project
        const projRes = await api(`/api/projects/${id}`);
        if (!projRes.ok) throw new Error(`Project fetch failed (${projRes.status})`);
        const proj = await projRes.json();

        
        // Releases (paged & server-filtered to this project)
const relRes = await api(
  `/api/releases/paged?projectId=${encodeURIComponent(id)}&limit=200&page=1`
);
if (!relRes.ok) throw new Error(`Releases fetch failed (${relRes.status})`);
const { items = [] } = await relRes.json();

// Artists (for autocomplete) — keep as-is
const artRes = await api('/api/artists');
const artList = artRes.ok ? await artRes.json() : [];
setAllArtists(Array.isArray(artList) ? artList : []);

// Same sort, but over 'items' returned by /paged
const linked = items.sort((a, b) => {
  if (!a.releaseDate && !b.releaseDate) return 0;
  if (!a.releaseDate) return 1;
  if (!b.releaseDate) return -1;
  return new Date(a.releaseDate) - new Date(b.releaseDate);
});

setProject(proj);

// restore project-level "Added All" status from the server
setProjectAddedToSheet(!!proj.isProjectAppendedToSheet);
setProjectDistrictAdded(!!proj.isProjectAppendedToDistrict);
setProjectKillphonicAdded(!!proj.isProjectAppendedToKillphonic);

setReleases(linked);

        // Project fields
        setProjTitle(proj.title || '');
        setProjType(proj.projectType || 'Album');
        setProjReleaseDate(proj.releaseDate ? proj.releaseDate.slice(0,10) : '');
        setProjCatalog(proj.projectCatalogueNumber || '');
        setProjUpc(proj.projectUpc || '');

        // Release initial state
        const state = {};
        for (const r of linked) {
          // Preseed mainArtists from r.artists (avoid duplicates)
          // Preseed mainArtists from r.artists (map ObjectId -> { _id, name } via artList)
const mainFromRelease = Array.isArray(r.artists)
  ? r.artists
      .map((a) => {
        // if already a populated object with name, keep it
        if (a && typeof a === 'object' && a._id && a.name) {
          return { _id: a._id, name: a.name, role: 'Primary' };
        }
        // otherwise look up by id in the loaded artists index
        const id = typeof a === 'string' ? a : a?._id;
        const found = (Array.isArray(artList) ? artList : []).find(
          (x) => String(x._id) === String(id)
        );
        return found ? { _id: found._id, name: found.name, role: 'Primary' } : null;
      })
      .filter(Boolean)
  : [];

          const extraArtists = r.otherPrimaryArtists
            ? String(r.otherPrimaryArtists).split(',').map(s => s.trim()).filter(Boolean)
            : [];

          state[r._id] = {
  title: r.title || '',
  releaseType: r.releaseType || 'Single',
  releaseDate: r.releaseDate ? r.releaseDate.slice(0, 10) : '',

  /* ── Identifiers ── */
  isrc: r.isrc || '',
  trackCatalogNumber: r.trackCatalogNumber || '',
  trackUpc: r.trackUpc || '',

  /* ── Version / ordering (asset form) ── */
  versionType: r.versionType || 'Original',
  remixerName: r.remixerName || '',
  altVersionName: r.altVersionName || '',
  tracklistNumber:
    typeof r.tracklistNumber === 'number' && Number.isFinite(r.tracklistNumber)
      ? r.tracklistNumber
      : '',

  /* ── Artists (chips) ── */
  mainArtists:
    Array.isArray(r.mainArtists) && r.mainArtists.length
      ? r.mainArtists
      : mainFromRelease,

  /* ── Contributors (master splits / side agreements) ── */
  contributors: Array.isArray(r.contributors) ? r.contributors : [],

  /* legacy simple names (kept for backward compat — hidden in UI) */
  additionalArtists: extraArtists,

  /* ── Credits / notes (asset form) ── */
  hasVocals: r.hasVocals ?? null,
  isExplicit: r.isExplicit ?? null,
  vocalists: r.vocalists || '',
  producer: r.producer || '',
  mixer: r.mixer || '',
  masteringEngineer: r.masteringEngineer || '',
  instrumentsAndPerformers: r.instrumentsAndPerformers || '',
  otherPrimaryArtists: r.otherPrimaryArtists || '',
  recordingCountry: r.recordingCountry || '',
  tiktokSnippet: r.tiktokSnippet || '',
  notes: r.notes || '',

  /* ── Writers (keep your structured array) ── */
  writers:
  Array.isArray(r.writers) && r.writers.length
    ? r.writers.map(w => ({
        legalName: (w?.legalName || ''),
        percentage: w?.percentage ?? '',
        pro: (w?.pro || ''),
        ipi: (w?.ipi || ''),
        publisherName: (w?.publisherName || ''),
        ckPublishing: (w?.ckPublishing === true ? true : w?.ckPublishing === false ? false : null),
        selfPublished: (w?.selfPublished === true ? true : w?.selfPublished === false ? false : null),
        // 👇 ensure it's always present for the input
        involvement: (w?.involvement || ''),
      }))
    : [
        {
          legalName: '',
          percentage: '',
          pro: '',
          ipi: '',
          publisherName: '',
          ckPublishing: null,
          selfPublished: null,
          involvement: '',
        },
      ],

  /* ── Uploads (unchanged) ── */
  uploads: {
    lyricsPdf: r.uploads?.lyricsPdf || '',
    losslessMaster: r.uploads?.losslessMaster || '',
    mp3Master: r.uploads?.mp3Master || '',
    losslessInstrumental: r.uploads?.losslessInstrumental || '',
    losslessCleanMaster: r.uploads?.losslessCleanMaster || '',
    acapella: r.uploads?.acapella || '',
    artwork: r.uploads?.artwork || '',
    artworkPsd: r.uploads?.artworkPsd || '',
    pressPics: r.uploads?.pressPics || '',
  },

    // server flag
  isSubmitted: !!r.isSubmitted,

  /* local only */
  pendingFiles: {},
};
        }
        setReleaseState(state);
      } catch (e) {
        setError(e.message || 'Load error');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const projectHeader = useMemo(() => {
    if (!project) return null;
    const artistNames = Array.isArray(project.artists)
      ? project.artists.map(a => a.name).filter(Boolean).join(', ')
      : '';
    return { title: project.title || '', artists: artistNames, cover: project.coverArt || '' };
  }, [project]);

  const showProjectArt = projType === 'Album' || projType === 'EP';

  const patchRelease = (relId, patch) =>
    setReleaseState(prev => ({ ...prev, [relId]: { ...prev[relId], ...patch } }));

  const toggleDetails = (relId) =>
    setOpenDetails(prev => ({ ...prev, [relId]: !prev[relId] }));

  const toggleIdents = (relId) =>
    setOpenIdents(prev => ({ ...prev, [relId]: !prev[relId] }));

  // writers helpers (delegated down)
  const onWriterChange = (relId, idx, field, value) =>
    patchRelease(relId, {
      writers: (releaseState[relId].writers || []).map((w, i) =>
        i === idx ? { ...w, [field]: value } : w
      ),
    });

  const addWriter = (relId) =>
    patchRelease(relId, {
      writers: [
        ...(releaseState[relId].writers || []),
        {
          legalName: '',
          percentage: '',
          pro: '',
          ipi: '',
          publisherName: '',
          ckPublishing: null,
          involvement: '',
        },
      ],
    });

  const removeWriter = (relId, idx) =>
    patchRelease(relId, {
      writers: (releaseState[relId].writers || []).filter((_, i) => i !== idx),
    });

// PUT to R2 with progress (use XHR because fetch has no upload progress)
function putWithProgress(url, file, contentType, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', contentType || 'application/octet-stream');

    if (xhr.upload && typeof onProgress === 'function') {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      };
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error('Network error'));
    xhr.send(file);
  });
}

  // uploads
  const uploadFile = async (file, relId, label, onProgress) => {
  try {
    // 1) sign
    const signRes = await api('/api/assets/sign', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        projectId: project?._id || id,
        releaseId: relId,
        label,
        filename: file.name,
        contentType: file.type,
        contentLength: file.size,
      }),
    });
    if (!signRes.ok) {
      const t = await signRes.text();
      throw new Error(`Sign failed (${signRes.status}): ${t}`);
    }
    const { url, key } = await signRes.json();

    // 2) upload to R2 WITH PROGRESS
    await putWithProgress(url, file, file.type, onProgress);

    // 3) build and persist uploads immediately
    const prevUploads = (releaseState[relId]?.uploads) || {};
    const nextUploads = { ...prevUploads, [label]: key };

    const res = await api(`/api/releases/${relId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ uploads: nextUploads }),
    });
    if (!res.ok) {
      const t = await res.text();
      throw new Error(`Persist uploads failed (${res.status}): ${t}`);
    }

    // 4) update local state
    patchRelease(relId, {
      uploads: nextUploads,
      pendingFiles: { ...(releaseState[relId]?.pendingFiles || {}), [label]: null },
    });
  } catch (e) {
    alert(e.message || 'Upload failed');
    // bubble so child can stop the spinner
    throw e;
  }
};

const handleViewFile = async (relId, label) => {
  const st = releaseState[relId] || {};
  const key = st.uploads?.[label];
  if (!key) return;

  // If it's already a full URL, open directly
  if (/^https?:\/\//i.test(key)) {
    window.open(key, '_blank');
    return;
  }

  // Otherwise it’s an R2 object key — get a signed GET
  try {
    const r = await api(`/api/assets/sign-get?key=${encodeURIComponent(key)}`);
    if (!r.ok) throw new Error(`Sign-get failed (${r.status})`);
    const { url } = await r.json();
    window.open(url, '_blank');
  } catch (e) {
    alert(e.message || 'Open failed');
  }
};

const handleDownloadFile = async (relId, label) => {
  const st = releaseState[relId] || {};
  const key = st.uploads?.[label];
  if (!key) return;

  // derive a nice filename with extension from the key
  const lastPart = String(key).split('/').pop() || 'file';
  const suggested = lastPart.includes('__') ? lastPart.split('__').pop() : lastPart;

  try {
    const r = await api(
      `/api/assets/sign-get?key=${encodeURIComponent(key)}&download=1&filename=${encodeURIComponent(suggested)}`
    );
    if (!r.ok) throw new Error(`Sign-get failed (${r.status})`);
    const { url } = await r.json();

    const a = document.createElement('a');
    a.href = url;
    a.download = suggested;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } catch (e) {
    alert(e.message || 'Download failed');
  }
};


const handleExportCsv = (relId, alsoAppend = false) => {
  const url = `${API_BASE}/api/export/releases/${relId}/csv${alsoAppend ? '?append=1' : ''}`;
  // open directly so there is no CORS / preflight
  window.open(url, '_blank', 'noopener,noreferrer');
};

const handleAppendAllToDistrict = async ({ onlySubmitted = false } = {}) => {
  try {
    setProjectDistrictAppending(true);

    const qs = new URLSearchParams();
    if (onlySubmitted) qs.set('onlySubmitted', '1');

    const r = await api(`/api/export/projects/${project._id}/district?${qs.toString()}`, { method: 'POST' });
    if (!r.ok) {
      const t = await r.text();
      throw new Error(`District add-all failed (${r.status}): ${t}`);
    }

    const { processed, added, appended, skipped } = await r.json();
    const addedCount = typeof added === 'number' ? added : (typeof appended === 'number' ? appended : 0);

    // Flip district UI flag and optimistically update project
    setProjectDistrictAdded(true);
    setProject((prev) =>
      prev
        ? {
            ...prev,
            isProjectAppendedToDistrict: true,
            projectAppendedToDistrictAt: new Date().toISOString(),
          }
        : prev
    );

    alert(`Added to Automatic District ✅
Processed: ${processed}
Added: ${addedCount}
Skipped: ${skipped}`);
  } catch (e) {
    alert(e.message || 'Failed to add all releases to Automatic District');
  } finally {
    setProjectDistrictAppending(false);
  }
};

const handleAppendReleaseToDistrict = async (relId) => {
  try {
    setRelDistrictAppending((p) => ({ ...p, [relId]: true }));

    const r = await api(`/api/export/releases/${relId}/district`, { method: 'POST' });
    if (!r.ok) {
      const t = await r.text();
      throw new Error(`District add (single) failed (${r.status}): ${t}`);
    }

    const { processed, added, appended, skipped } = await r.json();
    const addedCount = typeof added === 'number' ? added : (typeof appended === 'number' ? appended : 0);

    setRelDistrictAdded((p) => ({ ...p, [relId]: true }));

    alert(`Added to Automatic District ✅
Processed: ${processed}
Added: ${addedCount}
Skipped: ${skipped}`);
  } catch (e) {
    alert(e.message || 'Failed to add release to Automatic District');
  } finally {
    setRelDistrictAppending((p) => ({ ...p, [relId]: false }));
  }
  // reflect persisted flag in the releases list
setReleases((prev) =>
  prev.map((rel) =>
    String(rel._id) === String(relId)
      ? { ...rel, isAppendedToDistrict: true }
      : rel
  )
);
};

const handleAppendReleaseToKillphonic = async (relId) => {
  try {
    setRelKillphonicAppending((p) => ({ ...p, [relId]: true }));

    const r = await api(`/api/export/releases/${relId}/killphonic`, { method: 'POST' });
    if (!r.ok) {
      const t = await r.text();
      throw new Error(`Killphonic add (single) failed (${r.status}): ${t}`);
    }

    const { processed, added, skipped } = await r.json();

    // Reflect instantly
    setReleases((prev) =>
      prev.map((rel) =>
        String(rel._id) === String(relId)
          ? { ...rel, isAppendedToKillphonic: true }
          : rel
      )
    );
    setRelKillphonicAdded((p) => ({ ...p, [relId]: true }));

    alert(`Added to Automatic Killphonic ✅
Processed: ${processed}
Rows added (writers expanded): ${added}
Skipped: ${skipped}`);
  } catch (e) {
    alert(e.message || 'Failed to add release to Automatic Killphonic');
  } finally {
    setRelKillphonicAppending((p) => ({ ...p, [relId]: false }));
  }
};

const CREATORS_UPSERT_URL = 'https://sxvkbgwrziboygokvqmg.supabase.co/functions/v1/upsert-release';

const handleAppendReleaseToCreators = async (relId) => {
  const apiKey = process.env.REACT_APP_CREATORS_API_KEY;
  if (!apiKey || !apiKey.trim()) {
    alert('Creators API key is not configured. Set REACT_APP_CREATORS_API_KEY in your environment.');
    return;
  }

  const r = releases.find((rel) => String(rel._id) === String(relId));
  if (!r) {
    alert('Release not found');
    return;
  }

  const st = releaseState[relId] || {};

  const catalogue =
    st.trackCatalogNumber ||
    r.trackCatalogNumber ||
    r.assignedProjectCatalogNumber ||
    String(r._id);
  const mainArtists = st.mainArtists ?? r.mainArtists ?? [];
  const artist =
    Array.isArray(mainArtists) && mainArtists.length
      ? mainArtists.map((a) => (a && (a.name || a.title))).filter(Boolean).join(', ')
      : (Array.isArray(r.artists) && r.artists.length
          ? r.artists
              .map((a) =>
                typeof a === 'object' && a && (a.name || a.title) ? a.name || a.title : ''
              )
              .filter(Boolean)
              .join(', ')
          : '');
  const title = st.title || r.title || '';
  const rawDate = st.releaseDate || r.releaseDate;
  const release_date = rawDate
    ? (typeof rawDate === 'string' ? rawDate.slice(0, 10) : new Date(rawDate).toISOString().slice(0, 10))
    : '';
  const coverArt = r.coverArt || '';
  const cover_url =
    typeof coverArt === 'string' && (coverArt.startsWith('http://') || coverArt.startsWith('https://'))
      ? coverArt
      : '';

  // R2 object keys (preferred over time-limited URLs)
  const uploads = st.uploads || r.uploads || {};
  const audio_key = uploads.mp3Master || '';
  const lossless_key = uploads.losslessMaster || '';
  const cover_key = uploads.artwork || '';
  const lyrics_key = uploads.lyricsPdf || '';

  const body = {
    catalogue,
    artist: artist || 'Unknown',
    title: title || 'Untitled',
    isrc: st.isrc || r.isrc || '',
    release_date,
    fanlink: r.presaveLink || st.presaveLink || '',
    // Backward compatibility for the Creators function (keep these fields)
    audio_url: '',
    cover_url,
    type: st.releaseType || r.releaseType || 'Single',
    is_cover: false,
    lyrics_url: '',

    // New: keys so the other app can fetch directly from R2
    audio_key,
    lossless_key,
    cover_key,
    lyrics_key,
  };

  try {
    setRelCreatorsAppending((p) => ({ ...p, [relId]: true }));

    const response = await fetch(CREATORS_UPSERT_URL, {
      method: 'POST',
      headers: {
        'x-api-key': apiKey.trim(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(data?.error || data?.message || `Creators API error (${response.status})`);
    }

    // Persist "added" status so all users see it (like other export buttons)
    try {
      const upd = await api(`/api/releases/${relId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isAppendedToCreators: true }),
      });
      if (upd.ok) {
        setReleases((prev) =>
          prev.map((rel) =>
            String(rel._id) === String(relId)
              ? { ...rel, isAppendedToCreators: true }
              : rel
          )
        );
      }
    } catch {
      // non-fatal: the Creators upsert succeeded, but we couldn't persist the flag
    }

    setRelCreatorsAdded((p) => ({ ...p, [relId]: true })); // local instant feedback
    alert('Added to Creators ✅');
  } catch (e) {
    alert(e.message || 'Failed to add release to Creators');
  } finally {
    setRelCreatorsAppending((p) => ({ ...p, [relId]: false }));
  }
};

const handleAppendAllToSheet = async ({ onlySubmitted = false } = {}) => {
  try {
    setProjectAppending(true);

    const qs = new URLSearchParams();
    if (onlySubmitted) qs.set('onlySubmitted', '1');

    const r = await api(`/api/export/projects/${project._id}/sheet?${qs.toString()}`, { method: 'POST' });
    if (!r.ok) {
      const t = await r.text();
      throw new Error(`Append-all failed (${r.status}): ${t}`);
    }
    const { processed, appended, skipped, releaseIdsAdded } = await r.json();

    // Mark project-level success (don’t affect individual releases)
    setProjectAddedToSheet(true);

    // Mark per-release checkmarks immediately (server also persists this now)
    if (Array.isArray(releaseIdsAdded) && releaseIdsAdded.length) {
      setReleases((prev) =>
        prev.map((rel) =>
          releaseIdsAdded.includes(String(rel._id))
            ? { ...rel, isAppendedToSheet: true }
            : rel
        )
      );
    }

    // Optimistically update the loaded project (so UI matches without reload)
setProject((prev) =>
  prev
    ? {
        ...prev,
        isProjectAppendedToSheet: true,
        projectAppendedToSheetAt: new Date().toISOString(),
      }
    : prev
);

    alert(`Added to Google Sheet ✅\nProcessed: ${processed}\nAppended: ${appended}\nSkipped: ${skipped}`);
  } catch (e) {
    alert(e.message || 'Failed to add all releases to Sheet');
  } finally {
    setProjectAppending(false);
  }
};


const handleAppendAllToKillphonic = async ({ onlySubmitted = false } = {}) => {
  try {
    setProjectKillphonicAppending(true);

    const qs = new URLSearchParams();
    if (onlySubmitted) qs.set('onlySubmitted', '1');

    const r = await api(`/api/export/projects/${project._id}/killphonic?${qs.toString()}`, { method: 'POST' });
    if (!r.ok) {
      const t = await r.text();
      throw new Error(`Killphonic add-all failed (${r.status}): ${t}`);
    }

    const { processed, added, skipped } = await r.json();

    // Flip Killphonic UI flag and optimistically update project
    setProjectKillphonicAdded(true);
    setProject((prev) =>
      prev
        ? {
            ...prev,
            isProjectAppendedToKillphonic: true,
            projectAppendedToKillphonicAt: new Date().toISOString(),
          }
        : prev
    );

    alert(`Added to Automatic Killphonic ✅
Processed releases: ${processed}
Rows added (writers expanded): ${added}
Skipped: ${skipped}`);
  } catch (e) {
    alert(e.message || 'Failed to add all releases to Automatic Killphonic');
  } finally {
    setProjectKillphonicAppending(false);
  }
};

const handleAppendToSheet = async (relId, alreadyAdded = false) => {
  try {
    // If it's already marked as appended, ask before duplicating a row
    if (alreadyAdded) {
      const ok = window.confirm(
        'This release is already marked as “Added to Sheet”. Do you want to append another row anyway?'
      );
      if (!ok) return; // user cancelled — do nothing
    }

    const r = await api(`/api/export/releases/${relId}/sheet`, { method: 'POST' });
    if (!r.ok) {
      const t = await r.text();
      throw new Error(`Sheet append failed (${r.status}): ${t}`);
    }

    alert('Added to Google Sheet ✅');

    // Ensure everyone sees the checkmark immediately
    setReleases((prev) =>
      prev.map((rel) =>
        String(rel._id) === String(relId) ? { ...rel, isAppendedToSheet: true } : rel
      )
    );
  } catch (e) {
    alert(e.message || 'Failed to add to Google Sheet');
  }
};
  


// ---- Project cover-art upload (progress + replace + view/download) ----
const [projArtUploading, setProjArtUploading]   = useState(false);
const [projArtProgress,  setProjArtProgress]    = useState(0);
const [projArtReplacing, setProjArtReplacing]   = useState(false);
const [projArtPending,   setProjArtPending]     = useState(null);

const projectArtworkKey = (() => {
  const v = project?.uploads?.projectArtwork;
  if (!v) return null;
  return typeof v === 'string' ? v : v.key;
})();

const uploadProjectArtwork = async (fileOverride) => {
  try {
    const file = fileOverride || projArtPending;
    if (!file) { alert('Choose an image first.'); return; }

    setProjArtUploading(true);
    setProjArtProgress(0);

    // 1) sign upload
    const signRes = await api('/api/assets/sign', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        projectId: project?._id || id,
        releaseId: 'project',              // marker; backend just needs a value
        label:     'projectArtwork',
        filename:  file.name,
        contentType: file.type,
        contentLength: file.size,
      }),
    });
    if (!signRes.ok) {
      const t = await signRes.text();
      throw new Error(`Sign failed (${signRes.status}): ${t}`);
    }
    const { url, key } = await signRes.json();

    // 2) PUT with progress
    await putWithProgress(url, file, file.type, (pct)=>setProjArtProgress(pct));

    // 3) persist into Project.uploads (backend auto-builds coverArt via sharp)
    const nextUploads = {
      ...(project?.uploads || {}),
      projectArtwork: {
        key, originalName: file.name, size: file.size, type: file.type,
        uploadedAt: new Date().toISOString(),
      },
    };
    const saveRes = await api(`/api/projects/${project._id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ uploads: nextUploads }),
    });
    if (!saveRes.ok) {
      const t = await saveRes.text();
      throw new Error(`Save failed (${saveRes.status}): ${t}`);
    }
    const updated = await saveRes.json();
    setProject(updated);                    // cover image updates immediately
    setProjArtPending(null);
    setProjArtReplacing(false);
  } catch (e) {
    alert(e.message || 'Upload failed');
  } finally {
    setTimeout(()=>setProjArtUploading(false), 250);
  }
};

const viewProjectArtwork = async () => {
  if (!projectArtworkKey) return;
  if (/^https?:\/\//i.test(projectArtworkKey)) {
    window.open(projectArtworkKey, '_blank'); return;
  }
  const r = await api(`/api/assets/sign-get?key=${encodeURIComponent(projectArtworkKey)}`);
  if (!r.ok) { alert(`Open failed (${r.status})`); return; }
  const { url } = await r.json();
  window.open(url, '_blank');
};

const downloadProjectArtwork = async () => {
  if (!projectArtworkKey) return;
  const last      = (String(projectArtworkKey).split('/').pop() || 'Artwork.jpg');
  const suggested = last.includes('__') ? last.split('__').pop() : last;
  const r = await api(`/api/assets/sign-get?key=${encodeURIComponent(projectArtworkKey)}&download=1&filename=${encodeURIComponent(suggested)}`);
  if (!r.ok) { alert(`Download failed (${r.status})`); return; }
  const { url } = await r.json();
  const a = document.createElement('a');
  a.href = url; a.download = suggested;
  document.body.appendChild(a); a.click(); a.remove();
};

// ---- Project cover-art PSD upload (progress + replace + view/download) ----
const [projPsdUploading, setProjPsdUploading]   = useState(false);
// block parallel uploads between project cover + psd
const anyProjectUploading = projArtUploading || projPsdUploading;
const [projPsdProgress,  setProjPsdProgress]    = useState(0);
const [projPsdReplacing, setProjPsdReplacing]   = useState(false);
const [projPsdPending,   setProjPsdPending]     = useState(null);

const projectArtworkPsdKey = (() => {
  const v = project?.uploads?.projectArtworkPsd;
  if (!v) return null;
  return typeof v === 'string' ? v : v.key;
})();

const uploadProjectArtworkPsd = async (fileOverride) => {
  try {
    const file = fileOverride || projPsdPending;
    if (!file) { alert('Choose a PSD/ZIP first.'); return; }

    setProjPsdUploading(true);
    setProjPsdProgress(0);

    // 1) sign upload
    const signRes = await api('/api/assets/sign', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        projectId: project?._id || id,
        releaseId: 'project',              // marker
        label:     'projectArtworkPsd',
        filename:  file.name,
        contentType: file.type,
        contentLength: file.size,
      }),
    });
    if (!signRes.ok) {
      const t = await signRes.text();
      throw new Error(`Sign failed (${signRes.status}): ${t}`);
    }
    const { url, key } = await signRes.json();

    // 2) PUT with progress
    await putWithProgress(url, file, file.type || 'application/octet-stream',
      (pct)=>setProjPsdProgress(pct));

    // 3) persist into Project.uploads (no coverArt resize here)
    const nextUploads = {
      ...(project?.uploads || {}),
      projectArtworkPsd: {
        key, originalName: file.name, size: file.size, type: file.type,
        uploadedAt: new Date().toISOString(),
      },
    };
    const saveRes = await api(`/api/projects/${project._id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ uploads: nextUploads }),
    });
    if (!saveRes.ok) {
      const t = await saveRes.text();
      throw new Error(`Save failed (${saveRes.status}): ${t}`);
    }
    const updated = await saveRes.json();
    setProject(updated);
    setProjPsdPending(null);
    setProjPsdReplacing(false);
  } catch (e) {
    alert(e.message || 'Upload failed');
  } finally {
    setTimeout(()=>setProjPsdUploading(false), 250);
  }
};

const viewProjectArtworkPsd = async () => {
  if (!projectArtworkPsdKey) return;
  if (/^https?:\/\//i.test(projectArtworkPsdKey)) {
    window.open(projectArtworkPsdKey, '_blank'); return;
  }
  const r = await api(`/api/assets/sign-get?key=${encodeURIComponent(projectArtworkPsdKey)}`);
  if (!r.ok) { alert(`Open failed (${r.status})`); return; }
  const { url } = await r.json();
  window.open(url, '_blank');
};

const downloadProjectArtworkPsd = async () => {
  if (!projectArtworkPsdKey) return;
  const last      = (String(projectArtworkPsdKey).split('/').pop() || 'Artwork.psd');
  const suggested = last.includes('__') ? last.split('__').pop() : last;
  const r = await api(`/api/assets/sign-get?key=${encodeURIComponent(projectArtworkPsdKey)}&download=1&filename=${encodeURIComponent(suggested)}`);
  if (!r.ok) { alert(`Download failed (${r.status})`); return; }
  const { url } = await r.json();
  const a = document.createElement('a');
  a.href = url; a.download = suggested;
  document.body.appendChild(a); a.click(); a.remove();
};

// --- Inline edit: Project Title ---
const startEditProjectTitle = () => {
  setTitleDraft(projTitle || '');
  setIsTitleEditing(true);
};

const cancelEditProjectTitle = () => {
  setIsTitleEditing(false);
  setTitleDraft('');
};

const saveProjectTitle = async () => {
  const next = (titleDraft || '').trim();
  if (!next) return alert('Title cannot be empty.');
  try {
    setTitleSaving(true);
    const r = await api(`/api/projects/${project._id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: next }),
    });
    if (!r.ok) {
      const t = await r.text();
      throw new Error(`Save failed (${r.status}): ${t}`);
    }
    const updated = await r.json();
    // reflect in local UI
    setProject(updated);
    setProjTitle(updated.title || next);
    setIsTitleEditing(false);
  } catch (e) {
    alert(e.message || 'Failed to save title');
  } finally {
    setTitleSaving(false);
  }
};

  // --- Save Project Identifiers (locked by default; edit -> assign/cancel) ---
  const saveProjectIdentifiers = async () => {
    try {
      const trimmed = {
        projectCatalogueNumber: (projCatalog || '').trim(),
        projectUpc:             (projUpc || '').trim(),
      };

      const origCat = project?.projectCatalogueNumber || '';
      const origUpc = project?.projectUpc || '';
      const changingCat = origCat && origCat !== trimmed.projectCatalogueNumber;
      const changingUpc = origUpc && origUpc !== trimmed.projectUpc;
      if (changingCat || changingUpc) {
        const ok = window.confirm('These identifiers are important. Do you want to save the changes?');
        if (!ok) return;
      }

      const res = await api(`/api/projects/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(trimmed),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`Project identifiers save failed (${res.status}): ${txt}`);
      }
      const updated = await res.json();
      setProject(updated);
      setProjCatalog(updated.projectCatalogueNumber || '');
      setProjUpc(updated.projectUpc || '');
      setIdentsEditing(false);
      alert('Project identifiers saved.');
    } catch (e) {
      alert(e.message || 'Save error');
    }
  };

  const cancelProjectIdentifiers = () => {
    setProjCatalog(project?.projectCatalogueNumber || '');
    setProjUpc(project?.projectUpc || '');
    setIdentsEditing(false);
  };

  /* Save ONE release */
  const saveOne = async (relId) => {
    try {
      const r = releases.find(x => String(x._id) === String(relId));
      if (!r) throw new Error('Release not found in view.');
      const st = releaseState[relId];
      const isSingle = st.releaseType === 'Single';

      const payload = {
  // basics
  title: st.title,
  releaseType: st.releaseType,
  releaseDate: st.releaseDate || null,

  // identifiers
  isrc: st.isrc || '',
  trackCatalogNumber: isSingle ? (st.trackCatalogNumber || '') : '',
  trackUpc:           isSingle ? (st.trackUpc || '')           : '',

  // assigned project identifiers (echo)
  assignedProjectCatalogNumber: projCatalog || '',
  assignedProjectUpc:           projUpc || '',

  // ── NEW: Version / ordering (asset form) ──
  versionType:    st.versionType || 'Original',
  remixerName:    st.versionType === 'Remix' ? (st.remixerName || '') : '',
  altVersionName: st.versionType === 'Alternative Version' ? (st.altVersionName || '') : '',
  tracklistNumber:
    (projType === 'Album' || projType === 'EP')
      ? (st.tracklistNumber !== '' && st.tracklistNumber != null
          ? Number(st.tracklistNumber) || null
          : null)
      : null,

  // ── Artists (chips) ──
  mainArtists: Array.isArray(st.mainArtists)
    ? st.mainArtists.map(a => ({
        _id:  a?._id || undefined,
        name: a?.name || '',
        role: a?.role === 'Featuring' ? 'Featuring' : 'Primary',
      }))
    : [],

  // ── Contributors (master splits / side agreements) ──
  contributors: Array.isArray(st.contributors)
    ? st.contributors.map(c => ({
        name:              c?.name || '',
        splitPct:          c?.splitPct === '' || c?.splitPct == null ? 0 : Number(c.splitPct) || 0,
        needSideAgreement: (c?.needSideAgreement === true ? true
                              : c?.needSideAgreement === false ? false
                              : null),
        sideLegalName:     c?.sideLegalName || '',
        sideEmail:         c?.sideEmail || '',
      }))
    : [],

  // (legacy extra names string – keep your existing behavior)
  otherPrimaryArtists: (st.additionalArtists || [])
    .map(s => (s || '').trim())
    .filter(Boolean)
    .join(', '),

  // ── Credits / notes ──
  hasVocals:                st.hasVocals ?? null,
  isExplicit:               st.isExplicit ?? null,
  vocalists:                st.vocalists || '',
  producer:                 st.producer || '',
  mixer:                    st.mixer || '',
  masteringEngineer:        st.masteringEngineer || '',
  instrumentsAndPerformers: st.instrumentsAndPerformers || '',
  recordingCountry:         st.recordingCountry || '',
  tiktokSnippet:            st.tiktokSnippet || '',
  notes:                    st.notes || '',

  // writers + uploads (as you had)
  writers: st.writers,
  uploads: st.uploads,
};

      const res = await api(`/api/releases/${relId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`Release save failed (${res.status}): ${txt}`);
      }
      alert('Release saved!');
    } catch (e) {
      alert(e.message || 'Save error');
    }
  };

  const submitRelease = async (relId) => {
  try {
    setSubmitting((p) => ({ ...p, [relId]: true }));

    const r = await api(`/api/releases/${relId}/submit`, { method: 'POST' });

    if (!r.ok) {
      const t = await r.text();
      try {
        const j = JSON.parse(t);
        if (j?.missing && Array.isArray(j.missing) && j.missing.length) {
          alert(`Please fill all required fields:\n• ${j.missing.join('\n• ')}`);
        } else {
          alert(`Submit failed (${r.status}): ${t}`);
        }
      } catch {
        alert(`Submit failed (${r.status}): ${t}`);
      }
      return;
    }

    const { release: updated } = await r.json();

    setReleases((prev) =>
      prev.map((x) => (String(x._id) === String(relId) ? { ...x, isSubmitted: true } : x))
    );
    setReleaseState((prev) => ({
      ...prev,
      [relId]: { ...(prev[relId] || {}), isSubmitted: true },
    }));

    alert('Assets submitted ✅');
  } catch (e) {
    alert(e.message || 'Submit failed');
  } finally {
    setSubmitting((p) => ({ ...p, [relId]: false }));
  }
};

  if (loading) return <div style={{ color: '#fff', padding: 20 }}>Loading…</div>;
  if (error)   return <div style={{ color: '#fff', padding: 20 }}>Error: {error}</div>;
  if (!project) return <div style={{ color: '#fff', padding: 20 }}>Project not found</div>;

  const projectArtistsText = Array.isArray(project.artists)
    ? project.artists.map(a => a.name).filter(Boolean).join(', ')
    : '';

  return (
  <div style={{ padding: 20, color: '#fff' }}>
    {_iconCss}
      {/* top bar */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
        {!guest && (
        <button
          onClick={() => navigate(-1)}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#fff',
            fontSize: 24,
            cursor: 'pointer',
            marginRight: 12,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <svg width="32" height="32" viewBox="0 0 24 24" style={{ fill: '#333' }}>
            <path
              d="M10 6 L8.59 7.41 13.17 12 l-4.58 4.59L10 18 l6-6z"
              transform="rotate(180,12,12)"
            />
          </svg>
        </button>
        )}
        <h1 style={{ margin: 0 }}>Asset Collection</h1>
{!guest && (
        <button
  onClick={async () => {
    try {
      const r = await api('/api/guests/sign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId: project._id }),
      });
      if (!r.ok) throw new Error(await r.text());
      const { url } = await r.json();

      await navigator.clipboard.writeText(url);
      alert('Guest link copied to clipboard ✅');
    } catch (e) {
      alert('Failed to generate guest link: ' + e.message);
    }
  }}
  style={{
    marginLeft: 12,
    padding: '8px 12px',
    background: '#333',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
  }}
>
  Copy Link
</button>
)}

      </div>

      

      {/* Project (read-only for title, type, date) */}
      <Section title="Project">
  <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
    <img
      src={projectHeader.cover || emptyArt}
      alt=""
      style={{ width: 180, height: 180, objectFit: 'cover', borderRadius: 10 }}
    />

    <div>
      {/* Project Title (inline edit) */}
      <Row label="Project Title">
        {!isTitleEditing ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div
              style={{
                color: '#fff',
                fontWeight: 700,
                maxWidth: 480,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {projTitle || '—'}
            </div>
            <button
              onClick={startEditProjectTitle}
              title="Edit title"
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 28,
                height: 28,
                borderRadius: 6,
                backgroundColor: '#2a2a2a',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" style={{ fill: '#aaa' }}>
                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM21 7.04a1.2 1.2 0 0 0 0-1.68l-2.34-2.34a1.2 1.2 0 0 0-1.68 0l-1.84 1.84 3.75 3.75L21 7.04z" />
              </svg>
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <input
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              placeholder="Project title…"
              style={{ ...input.wide, maxWidth: 420 }}
              disabled={titleSaving}
            />
            <button
              onClick={saveProjectTitle}
              disabled={titleSaving}
              style={{
                padding: '8px 12px',
                background: '#28a745',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                cursor: titleSaving ? 'not-allowed' : 'pointer',
                fontWeight: 700,
              }}
            >
              {titleSaving ? 'Saving…' : 'Save'}
            </button>
            <button
              onClick={cancelEditProjectTitle}
              disabled={titleSaving}
              style={{
                padding: '8px 12px',
                background: '#555',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        )}
      </Row>

      {/* Project artists (read-only) */}
      <Row label="Project Artists">
        <div style={{ color: '#ccc', maxWidth: 600 }}>{projectArtistsText || '—'}</div>
      </Row>

      {/* Project Type */}
      <Row label="Project Type">
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Chip active colorActive="#555" disabled>
            {projType || '—'}
          </Chip>
        </div>
      </Row>

      {/* Release Date */}
      <Row label="Release Date">
        <div style={{ color: '#ccc' }}>{fmtDate(projReleaseDate)}</div>
      </Row>

      {/* Project artwork (only for Album/EP) */}
      {showProjectArt && (
        <>
          {/* Project Cover Art */}
          <Row label="Project Cover Art">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <div style={{ width: input.base.width }}>
                {projectArtworkKey && !projArtReplacing ? (
                  <span
                    style={{
                      display: 'inline-block',
                      width: '100%',
                      color: '#8bc34a',
                      background: '#1f2a1f',
                      border: 'none',
                      padding: 8,
                      borderRadius: 6,
                      fontSize: 12,
                      textAlign: 'center',
                      boxSizing: 'border-box',
                    }}
                  >
                    File uploaded
                  </span>
                ) : (
                  <input
  type="file"
  accept="image/jpeg,image/jpg,image/png"
  onChange={async (e) => {
  const file = e.target.files?.[0] || null;
  if (!file) return;

  if (anyProjectUploading && !projArtUploading) {
    alert('Another project file is still uploading. Please wait until it finishes.');
    e.target.value = '';
    return;
  }

  setProjArtPending(file);
  try {
    await uploadProjectArtwork(file); // 👈 pass the file directly
  } catch (_) {}
}}
  disabled={projArtUploading || (anyProjectUploading && !projArtUploading)}
  style={{
    ...input.base,
    width: '100%',
    opacity: (projArtUploading || (anyProjectUploading && !projArtUploading)) ? 0.7 : 1,
    boxSizing: 'border-box',
  }}
/>
                )}
              </div>

              <button
                onClick={() => {
                  if (projArtUploading) return;
                  if (projectArtworkKey && !projArtReplacing) {
                    setProjArtReplacing(true);
                    setProjArtProgress(0);
                    return;
                  }
                  uploadProjectArtwork();
                }}
                disabled={projArtUploading || (anyProjectUploading && !projArtUploading)}
                style={{
  padding: '8px 12px',
  background: (projArtUploading || (anyProjectUploading && !projArtUploading)) ? '#555' : '#333',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  cursor: (projArtUploading || (anyProjectUploading && !projArtUploading)) ? 'not-allowed' : 'pointer',
  minWidth: 110,
}}
              >
                {projArtUploading
                  ? `Uploading ${Math.max(0, Math.min(100, projArtProgress))}%`
                  : projectArtworkKey && !projArtReplacing
                  ? 'Replace'
                  : 'Upload'}
              </button>

              {projectArtworkKey && projArtReplacing && !projArtUploading && (
                <button
                  onClick={() => {
                    setProjArtReplacing(false);
                    setProjArtPending(null);
                  }}
                  style={{
                    padding: '8px 12px',
                    background: '#555',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
              )}

              {projectArtworkKey && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 4 }}>
                  {/* View */}
                  {/^https?:\/\//i.test(projectArtworkKey) ? (
                    <a
                      href={projectArtworkKey}
                      target="_blank"
                      rel="noreferrer"
                      title="View"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 32,
                        height: 32,
                        borderRadius: 6,
                        background: '#222',
                        border: '1px solid #2f2f2f',
                      }}
                    >
                      <ViewIcon style={{ width: 16, height: 16, fill: '#aaa' }} />
                    </a>
                  ) : (
                    <button
                      type="button"
                      onClick={viewProjectArtwork}
                      title="View"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 32,
                        height: 32,
                        borderRadius: 6,
                        background: '#222',
                        border: '1px solid #2f2f2f',
                        cursor: 'pointer',
                      }}
                    >
                      <ViewIcon style={{ width: 16, height: 16, fill: '#aaa' }} />
                    </button>
                  )}

                  {/* Download */}
                  <button
                    type="button"
                    onClick={downloadProjectArtwork}
                    title="Download"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 32,
                      height: 32,
                      borderRadius: 6,
                      background: '#222',
                      border: '1px solid #2f2f2f',
                      cursor: 'pointer',
                    }}
                  >
                    <DownloadIcon style={{ width: 16, height: 16, fill: '#aaa' }} />
                  </button>
                </div>
              )}
            </div>

            {projArtUploading && (
              <div style={{ marginTop: 6, width: input.base.width }}>
                <div style={{ height: 6, background: '#222', borderRadius: 4, overflow: 'hidden' }}>
                  <div
                    style={{
                      height: '100%',
                      width: `${Math.max(0, Math.min(100, projArtProgress))}%`,
                      background: '#28a745',
                      transition: 'width .2s linear',
                    }}
                  />
                </div>
              </div>
            )}
          </Row>

          {/* Project Cover Art PSD */}
          <Row label="Project Cover Art PSD">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <div style={{ width: input.base.width, flex: '0 0 auto' }}>
                {projectArtworkPsdKey && !projPsdReplacing ? (
                  <span
                    style={{
                      display: 'inline-block',
                      width: '100%',
                      color: '#8bc34a',
                      background: '#1f2a1f',
                      border: 'none',
                      padding: 8,
                      borderRadius: 6,
                      fontSize: 12,
                      textAlign: 'center',
                      boxSizing: 'border-box',
                    }}
                  >
                    File uploaded
                  </span>
                ) : (
                  <input
  type="file"
  accept=".psd,.psb,application/vnd.adobe.photoshop,.zip,application/zip,application/x-zip-compressed"
  onChange={async (e) => {
    const file = e.target.files?.[0] || null;
    if (!file) return;

    if (anyProjectUploading && !projPsdUploading) {
      alert('Another project file is still uploading. Please wait until it finishes.');
      e.target.value = '';
      return;
    }

    setProjPsdPending(file);
    try {
      await uploadProjectArtworkPsd(file);
    } catch (_) {
      // already alerted inside helper
    }
  }}
  disabled={projPsdUploading || (anyProjectUploading && !projPsdUploading)}
  style={{
    ...input.base,
    width: '100%',
    opacity: (projPsdUploading || (anyProjectUploading && !projPsdUploading)) ? 0.7 : 1,
    boxSizing: 'border-box',
  }}
/>
                )}
              </div>

              <button
                onClick={() => {
                  if (projPsdUploading) return;
                  if (projectArtworkPsdKey && !projPsdReplacing) {
                    setProjPsdReplacing(true);
                    setProjPsdProgress(0);
                    return;
                  }
                  uploadProjectArtworkPsd();
                }}
                disabled={projPsdUploading || (anyProjectUploading && !projPsdUploading)}
                style={{
  flex: '0 0 auto',
  padding: '8px 12px',
  background: (projPsdUploading || (anyProjectUploading && !projPsdUploading)) ? '#555' : '#333',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  cursor: (projPsdUploading || (anyProjectUploading && !projPsdUploading)) ? 'not-allowed' : 'pointer',
  minWidth: 110,
}}
              >
                {projPsdUploading
                  ? `Uploading ${Math.max(0, Math.min(100, projPsdProgress))}%`
                  : projectArtworkPsdKey && !projPsdReplacing
                  ? 'Replace'
                  : 'Upload'}
              </button>

              {projectArtworkPsdKey && projPsdReplacing && !projPsdUploading && (
                <button
                  onClick={() => {
                    setProjPsdReplacing(false);
                    setProjPsdPending(null);
                  }}
                  style={{
                    flex: '0 0 auto',
                    padding: '8px 12px',
                    background: '#555',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
              )}

              {projectArtworkPsdKey && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 4, flex: '0 0 auto' }}>
                  <button
                    type="button"
                    onClick={viewProjectArtworkPsd}
                    title="View"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 32,
                      height: 32,
                      borderRadius: 6,
                      background: '#222',
                      border: '1px solid #2f2f2f',
                      cursor: 'pointer',
                    }}
                  >
                    <ViewIcon style={{ width: 16, height: 16, fill: '#aaa' }} />
                  </button>
                  <button
                    type="button"
                    onClick={downloadProjectArtworkPsd}
                    title="Download"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 32,
                      height: 32,
                      borderRadius: 6,
                      background: '#222',
                      border: '1px solid #2f2f2f',
                      cursor: 'pointer',
                    }}
                  >
                    <DownloadIcon style={{ width: 16, height: 16, fill: '#aaa' }} />
                  </button>
                </div>
              )}
            </div>

            {projPsdUploading && (
              <div style={{ marginTop: 6, width: input.base.width }}>
                <div style={{ height: 6, background: '#222', borderRadius: 4, overflow: 'hidden' }}>
                  <div
                    style={{
                      height: '100%',
                      width: `${Math.max(0, Math.min(100, projPsdProgress))}%`,
                      background: '#28a745',
                      transition: 'width .2s linear',
                    }}
                  />
                </div>
              </div>
            )}
          </Row>
        </>
      )}

      {/* Show Identifiers toggle (aligned under the value column) */}
      {!guest && (
      <Row label="">
        <button
          onClick={() => setOpenProjIdents(!openProjIdents)}
          style={{
            background: '#444',
            color: '#fff',
            border: 'none',
            borderRadius: 4,
            padding: '6px 12px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {openProjIdents ? 'Hide Identifiers' : 'Show Identifiers'}
        </button>
      </Row>
      )}

      {/* Identifiers content */}
      {!guest && openProjIdents && (
        <div style={{ marginTop: 10 }}>
          {project?.projectCatalogueNumber || project?.projectUpc ? (
            <>
              <Row label=" Project Catalogue #" >
  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
    <input
      value={projCatalog}
      onChange={(e) => setProjCatalog(e.target.value)}
      style={{ ...input.base, opacity: identsEditing ? 1 : 0.7 }}
      readOnly={!identsEditing}
    />
    {!guest && (identsEditing || !projCatalog) && (
      <button
        disabled={assigning[`${project._id}_projCat`]}
        onClick={async () => {
          setAssigning((p) => ({ ...p, [`${project._id}_projCat`]: true }));
          try {
            const resp = await api('/api/identifiers/assign-project', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ projectId: project._id, kind: 'projectCatalog' }),
            });
            if (!resp.ok) throw new Error(await resp.text());
            const { value } = await resp.json();
            setProjCatalog(value);
            // keep server & local project in sync (already saved server-side, but reflect locally)
            setProject((prev) => prev ? { ...prev, projectCatalogueNumber: value } : prev);
            alert('Project Catalogue # assigned ✅');
          } catch (e) {
            alert(e.message || 'Failed to assign Project Catalogue #');
          } finally {
            setAssigning((p) => ({ ...p, [`${project._id}_projCat`]: false }));
          }
        }}
        style={{
          padding: '6px 10px',
          background: assigning[`${project._id}_projCat`] ? '#555' : '#333',
          color: '#fff',
          border: 'none',
          borderRadius: 6,
          cursor: assigning[`${project._id}_projCat`] ? 'not-allowed' : 'pointer',
          fontWeight: 600,
        }}
        title="Assign from Sheet"
      >
        {assigning[`${project._id}_projCat`] ? 'Assigning…' : 'Assign number'}
      </button>
    )}
  </div>
</Row>

              <Row label="Project UPC">
  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
    <input
      value={projUpc}
      onChange={(e) => setProjUpc(e.target.value)}
      style={{ ...input.base, opacity: identsEditing ? 1 : 0.7 }}
      readOnly={!identsEditing}
    />
    {!guest && (identsEditing || !projUpc) && (
      <button
        disabled={assigning[`${project._id}_projUpc`]}
        onClick={async () => {
          setAssigning((p) => ({ ...p, [`${project._id}_projUpc`]: true }));
          try {
            const resp = await api('/api/identifiers/assign-project', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ projectId: project._id, kind: 'projectUpc' }),
            });
            if (!resp.ok) throw new Error(await resp.text());
            const { value } = await resp.json();
            setProjUpc(value);
            setProject((prev) => prev ? { ...prev, projectUpc: value } : prev);
            alert('Project UPC assigned ✅');
          } catch (e) {
            alert(e.message || 'Failed to assign Project UPC');
          } finally {
            setAssigning((p) => ({ ...p, [`${project._id}_projUpc`]: false }));
          }
        }}
        style={{
          padding: '6px 10px',
          background: assigning[`${project._id}_projUpc`] ? '#555' : '#333',
          color: '#fff',
          border: 'none',
          borderRadius: 6,
          cursor: assigning[`${project._id}_projUpc`] ? 'not-allowed' : 'pointer',
          fontWeight: 600,
        }}
        title="Assign from Sheet"
      >
        {assigning[`${project._id}_projUpc`] ? 'Assigning…' : 'Assign number'}
      </button>
    )}
  </div>
</Row>

              <Row label="" compact>
                {!identsEditing ? (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => setIdentsEditing(true)}
                      style={{
                        padding: '8px 12px',
                        background: '#777',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 6,
                        cursor: 'pointer',
                        fontWeight: 600,
                      }}
                    >
                      Edit
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={saveProjectIdentifiers}
                      style={{
                        padding: '8px 12px',
                        background: '#28a745',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 6,
                        cursor: 'pointer',
                        fontWeight: 700,
                      }}
                    >
                      Assign
                    </button>
                    <button
                      onClick={cancelProjectIdentifiers}
                      style={{
                        padding: '8px 12px',
                        background: '#555',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 6,
                        cursor: 'pointer',
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </Row>
            </>
          ) : (
            <>
              <Row label="Catalogue #">
  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
    <input
      value={projCatalog}
      onChange={(e) => setProjCatalog(e.target.value)}
      placeholder="e.g. CK001"
      style={input.base}
      readOnly={false}
    />
    {!guest && !projCatalog && (
      <button
        disabled={assigning[`${project._id}_projCat`]}
        onClick={async () => {
          setAssigning((p) => ({ ...p, [`${project._id}_projCat`]: true }));
          try {
            const resp = await api('/api/identifiers/assign-project', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ projectId: project._id, kind: 'projectCatalog' }),
            });
            if (!resp.ok) throw new Error(await resp.text());
            const { value } = await resp.json();
            setProjCatalog(value);
            setProject((prev) => prev ? { ...prev, projectCatalogueNumber: value } : prev);
            alert('Project Catalogue # assigned ✅');
          } catch (e) {
            alert(e.message || 'Failed to assign Project Catalogue #');
          } finally {
            setAssigning((p) => ({ ...p, [`${project._id}_projCat`]: false }));
          }
        }}
        style={{
          padding: '6px 10px',
          background: assigning[`${project._id}_projCat`] ? '#555' : '#333',
          color: '#fff',
          border: 'none',
          borderRadius: 6,
          cursor: assigning[`${project._id}_projCat`] ? 'not-allowed' : 'pointer',
          fontWeight: 600,
        }}
        title="Assign from Sheet"
      >
        {assigning[`${project._id}_projCat`] ? 'Assigning…' : 'Assign number'}
      </button>
    )}
  </div>
</Row>

              <Row label="UPC">
  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
    <input
      value={projUpc}
      onChange={(e) => setProjUpc(e.target.value)}
      placeholder="e.g. 4260571127742"
      style={input.base}
      readOnly={false}
    />
    {!guest && !projUpc && (
      <button
        disabled={assigning[`${project._id}_projUpc`]}
        onClick={async () => {
          setAssigning((p) => ({ ...p, [`${project._id}_projUpc`]: true }));
          try {
            const resp = await api('/api/identifiers/assign-project', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ projectId: project._id, kind: 'projectUpc' }),
            });
            if (!resp.ok) throw new Error(await resp.text());
            const { value } = await resp.json();
            setProjUpc(value);
            setProject((prev) => prev ? { ...prev, projectUpc: value } : prev);
            alert('Project UPC assigned ✅');
          } catch (e) {
            alert(e.message || 'Failed to assign Project UPC');
          } finally {
            setAssigning((p) => ({ ...p, [`${project._id}_projUpc`]: false }));
          }
        }}
        style={{
          padding: '6px 10px',
          background: assigning[`${project._id}_projUpc`] ? '#555' : '#333',
          color: '#fff',
          border: 'none',
          borderRadius: 6,
          cursor: assigning[`${project._id}_projUpc`] ? 'not-allowed' : 'pointer',
          fontWeight: 600,
        }}
        title="Assign from Sheet"
      >
        {assigning[`${project._id}_projUpc`] ? 'Assigning…' : 'Assign number'}
      </button>
    )}
  </div>
</Row>

              <Row label="" compact>
                <div>
                  <button
                    onClick={saveProjectIdentifiers}
                    style={{
                      padding: '8px 12px',
                      background: '#28a745',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 6,
                      cursor: 'pointer',
                      fontWeight: 700,
                    }}
                  >
                    Assign
                  </button>
                </div>
              </Row>
            </>
          )}
        </div>
      )}
    </div>
  </div>
</Section>

      {/* Releases */}
<Section title="Linked Releases">
  {!guest && (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginBottom: 8 }}>
      <button
  onClick={() => handleAppendAllToSheet()}
  disabled={projectAppending}
  style={{
    padding: '8px 12px',
    background: projectAppending ? '#555' : '#444',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    cursor: projectAppending ? 'not-allowed' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    opacity: projectAppending ? 0.8 : 1,
  }}
  title="Append ALL releases of this project to Google Sheet (duplicates allowed)"
>
  {projectAddedToSheet && (
    <CheckCircleIcon className="ck-check" style={{ width: 16, height: 16 }} />
  )}
  {projectAddedToSheet
    ? 'Added All to Sheet'
    : projectAppending
    ? 'Adding…'
    : 'Add All to Sheet (Project)'}
</button>

      {/*
      // Optional: submitted-only variant
      <button
        onClick={() => handleAppendAllToSheet({ onlySubmitted: true })}
        style={{ padding:'8px 12px', background:'#333', color:'#fff', border:'none', borderRadius:6, cursor:'pointer' }}
        title="Append only submitted releases"
      >
        Add Submitted Only
      </button>
      */}

      <button
  onClick={() => handleAppendAllToDistrict()}
  disabled={projectDistrictAppending}
  style={{
    padding: '8px 12px',
    background: projectDistrictAppending ? '#555' : '#444',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    cursor: projectDistrictAppending ? 'not-allowed' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    opacity: projectDistrictAppending ? 0.8 : 1,
  }}
  title={
    projectDistrictAdded && project?.projectAppendedToDistrictAt
      ? `Added on ${new Date(project.projectAppendedToDistrictAt).toLocaleString()}`
      : 'Add ALL releases of this project to the "Automatic District" tab'
  }
>
  {projectDistrictAdded && (
    <CheckCircleIcon className="ck-check" style={{ width: 16, height: 16 }} />
  )}
  {projectDistrictAdded
    ? 'Added to District'
    : projectDistrictAppending
    ? 'Adding…'
    : 'Add all to District'}
</button>

<button
  onClick={() => handleAppendAllToKillphonic()}
  disabled={projectKillphonicAppending}
  style={{
    padding: '8px 12px',
    background: projectKillphonicAppending ? '#555' : '#444',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    cursor: projectKillphonicAppending ? 'not-allowed' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    opacity: projectKillphonicAppending ? 0.8 : 1,
  }}
  title={
    projectKillphonicAdded && project?.projectAppendedToKillphonicAt
      ? `Added on ${new Date(project.projectAppendedToKillphonicAt).toLocaleString()}`
      : 'Add ALL releases of this project to the "Automatic Killphonic" tab'
  }
>
  {projectKillphonicAdded && (
    <CheckCircleIcon className="ck-check" style={{ width: 16, height: 16 }} />
  )}
  {projectKillphonicAdded
    ? 'Added to Killphonic'
    : projectKillphonicAppending
    ? 'Adding…'
    : 'Add all to Killphonic'}
</button>
    </div>

    
  )}
        {releases.map((r) => {
          const st = releaseState[r._id] || {};
          const isSingle = st.releaseType === 'Single';
          const hasServerIdentifiers = !!(r.isrc || r.trackCatalogNumber || r.trackUpc);

          return (
            <div
              key={r._id}
              style={{
                border: 'none',
                borderRadius: 10,
                padding: 12,
                background: '#171717',
                marginBottom: 12,
              }}
            >
              {/* header section with cover */}
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 8 }}>
                <img
                  src={r.coverArt || emptyArt}
                  alt=""
                  style={{ width: 90, height: 90, objectFit: 'cover', borderRadius: 8 }}
                />

                <div>
                  {/* READ-ONLY Release Title */}
                  <Row label="Release Title" compact>
                    <div style={{ color: '#fff', fontWeight: 600 }}>
                      {st.title || r.title || '—'}
                    </div>
                  </Row>

                  {/* READ-ONLY Artists */}
                  <Row label="Artists" compact>
                    <div style={{ color: '#bbb', maxWidth: 420 }}>
                      {Array.isArray(st.mainArtists) && st.mainArtists.length
                        ? st.mainArtists.map(a=>a?.name).filter(Boolean).join(', ')
                        : '—'}
                    </div>
                  </Row>

                  {/* READ-ONLY Type & Date */}
                  <Row label="Release Type" compact>
                    <Chip active colorActive="#555" disabled>
                      {st.releaseType || r.releaseType || '—'}
                    </Chip>
                  </Row>

                  <Row label="Release Date" compact>
                    <div style={{ color: '#ccc' }}>
                      {fmtDate(st.releaseDate || r.releaseDate)}
                    </div>
                  </Row>

                  {/* READ-ONLY Track ISRC */}
<Row label="Track ISRC" compact>
  <div style={{ color: '#ccc' }}>
    {r.isrc ? r.isrc : 'Not yet assigned'}
  </div>
</Row>

{/* READ-ONLY Track UPC */}
<Row label="Track UPC" compact>
  <div style={{ color: '#ccc' }}>
    {r.trackUpc ? r.trackUpc : 'Not yet assigned'}
  </div>
</Row>

                  {/* Identifiers collapsible (now with Assign/Edit/Cancel) */}
{!guest && (
<Row label="">
  <button
    onClick={() => toggleIdents(r._id)}
    style={{
      background: '#444',
      color: '#fff',
      border: 'none',
      borderRadius: 4,
      padding: '6px 12px',
      fontWeight: 600,
      cursor: 'pointer',
    }}
  >
    {openIdents[r._id] ? 'Hide Identifiers' : 'Show Identifiers'}
  </button>
</Row>
)}
                </div>
              </div>

              
              {!guest && openIdents[r._id] && (
    <div style={{ marginTop: 8, marginLeft: 102 }}>

    {/* If identifiers already exist → lock until Edit */}
{hasServerIdentifiers ? (
  <>
    {isSingle ? (
      <>
        <Row label="Track Catalogue #" compact>
  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
    <input
      value={st.trackCatalogNumber || ''}
      onChange={(e) => patchRelease(r._id, { trackCatalogNumber: e.target.value })}
      placeholder="e.g. CK001"
      style={input.sm}
      readOnly={false}
    />
    {!guest && !st.trackCatalogNumber && (
      <button
        disabled={assigning[`${r._id}_trackCatalog`]}
        onClick={async () => {
          setAssigning((p) => ({ ...p, [`${r._id}_trackCatalog`]: true }));
          try {
            const resp = await api('/api/identifiers/assign', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ releaseId: r._id, kind: 'trackCatalog' }),
            });
            if (!resp.ok) throw new Error(await resp.text());
            const { value } = await resp.json();
            patchRelease(r._id, { trackCatalogNumber: value });
            await saveOne(r._id);
            alert('Track Catalogue # assigned ✅');
          } catch (e) {
            alert(e.message || 'Failed to assign Track Catalogue #');
          } finally {
            setAssigning((p) => ({ ...p, [`${r._id}_trackCatalog`]: false }));
          }
        }}
        style={{
          padding: '6px 10px',
          background: assigning[`${r._id}_trackCatalog`] ? '#555' : '#333',
          color: '#fff',
          border: 'none',
          borderRadius: 6,
          cursor: assigning[`${r._id}_trackCatalog`] ? 'not-allowed' : 'pointer',
          fontWeight: 600,
        }}
        title="Assign from Sheet"
      >
        {assigning[`${r._id}_trackCatalog`] ? 'Assigning…' : 'Assign number'}
      </button>
    )}
  </div>
</Row>
      </>
    ) : null}

    <Row label="ISRC" compact>
  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
    <input
      value={st.isrc || ''}
      onChange={(e) => patchRelease(r._id, { isrc: e.target.value })}
      placeholder="e.g. DEWO71700870"
      style={input.sm}
      readOnly={false}
    />
    {!guest && !st.isrc && (
      <button
        disabled={assigning[`${r._id}_isrc`]}
        onClick={async () => {
          setAssigning((p) => ({ ...p, [`${r._id}_isrc`]: true }));
          try {
            const resp = await api('/api/identifiers/assign', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ releaseId: r._id, kind: 'isrc' }),
            });
            if (!resp.ok) throw new Error(await resp.text());
            const { value } = await resp.json();
            patchRelease(r._id, { isrc: value });
            await saveOne(r._id);
            alert('ISRC assigned ✅');
          } catch (e) {
            alert(e.message || 'Failed to assign ISRC');
          } finally {
            setAssigning((p) => ({ ...p, [`${r._id}_isrc`]: false }));
          }
        }}
        style={{
          padding: '6px 10px',
          background: assigning[`${r._id}_isrc`] ? '#555' : '#333',
          color: '#fff',
          border: 'none',
          borderRadius: 6,
          cursor: assigning[`${r._id}_isrc`] ? 'not-allowed' : 'pointer',
          fontWeight: 600,
        }}
        title="Assign from Sheet"
      >
        {assigning[`${r._id}_isrc`] ? 'Assigning…' : 'Assign number'}
      </button>
    )}
  </div>
</Row>

    {isSingle ? (
      <>
        <Row label="Track UPC" compact>
  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
    <input
      value={st.trackUpc || ''}
      onChange={(e) => patchRelease(r._id, { trackUpc: e.target.value })}
      placeholder="e.g. 4260571127742"
      style={input.sm}
      readOnly={false}
    />
    {!guest && !st.trackUpc && (
      <button
        disabled={assigning[`${r._id}_trackUpc`]}
        onClick={async () => {
          setAssigning((p) => ({ ...p, [`${r._id}_trackUpc`]: true }));
          try {
            const resp = await api('/api/identifiers/assign', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ releaseId: r._id, kind: 'trackUpc' }),
            });
            if (!resp.ok) throw new Error(await resp.text());
            const { value } = await resp.json();
            patchRelease(r._id, { trackUpc: value });
            await saveOne(r._id);
            alert('Track UPC assigned ✅');
          } catch (e) {
            alert(e.message || 'Failed to assign Track UPC');
          } finally {
            setAssigning((p) => ({ ...p, [`${r._id}_trackUpc`]: false }));
          }
        }}
        style={{
          padding: '6px 10px',
          background: assigning[`${r._id}_trackUpc`] ? '#555' : '#333',
          color: '#fff',
          border: 'none',
          borderRadius: 6,
          cursor: assigning[`${r._id}_trackUpc`] ? 'not-allowed' : 'pointer',
          fontWeight: 600,
        }}
        title="Assign from Sheet"
      >
        {assigning[`${r._id}_trackUpc`] ? 'Assigning…' : 'Assign number'}
      </button>
    )}
  </div>
</Row>
      </>
    ) : null}

                      <Row label="Assigned Project Catalogue #" compact>
                        <input value={projCatalog || ''} readOnly style={{ ...input.sm, opacity: 0.7 }} />
                      </Row>
                      <Row label="Assigned Project UPC" compact>
                        <input value={projUpc || ''} readOnly style={{ ...input.sm, opacity: 0.7 }} />
                      </Row>

                      {/* Buttons aligned exactly with inputs */}
                      <Row label="" compact>
                        {!relIdentsEditing[r._id] ? (
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button
                              onClick={() => setRelEditing(r._id, true)}
                              style={{
                                padding: '8px 12px',
                                background: '#777',
                                color: '#fff',
                                border: 'none',
                                borderRadius: 6,
                                cursor: 'pointer',
                                fontWeight: 600,
                              }}
                            >
                              Edit
                            </button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button
                              onClick={() => {
                                saveOne(r._id).then(() => setRelEditing(r._id, false));
                              }}
                              style={{
                                padding: '8px 12px',
                                background: '#28a745',
                                color: '#fff',
                                border: 'none',
                                borderRadius: 6,
                                cursor: 'pointer',
                                fontWeight: 700,
                              }}
                            >
                              Assign
                            </button>
                            <button
                              onClick={() => {
                                // revert to server values from original release object r
                                patchRelease(r._id, {
                                  isrc: r.isrc || '',
                                  trackCatalogNumber: r.trackCatalogNumber || '',
                                  trackUpc: r.trackUpc || '',
                                });
                                setRelEditing(r._id, false);
                              }}
                              style={{
                                padding: '8px 12px',
                                background: '#555',
                                color: '#fff',
                                border: 'none',
                                borderRadius: 6,
                                cursor: 'pointer',
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                      </Row>
                    </>
                  ) : (
                    /* No identifiers yet → directly editable with Assign */
                    <>
  {isSingle ? (
    <>
      <Row label="Track Catalogue #" compact>
  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
    <input
      value={st.trackCatalogNumber || ''}
      onChange={(e) => patchRelease(r._id, { trackCatalogNumber: e.target.value })}
      placeholder="e.g. CK001"
      style={input.sm}
      readOnly={false}
    />
    {!guest && !st.trackCatalogNumber && (
      <button
        disabled={assigning[`${r._id}_trackCatalog`]}
        onClick={async () => {
          setAssigning((p) => ({ ...p, [`${r._id}_trackCatalog`]: true }));
          try {
            const resp = await api('/api/identifiers/assign', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ releaseId: r._id, kind: 'trackCatalog' }),
            });
            if (!resp.ok) throw new Error(await resp.text());
            const { value } = await resp.json();
            patchRelease(r._id, { trackCatalogNumber: value });
            await saveOne(r._id);
            alert('Track Catalogue # assigned ✅');
          } catch (e) {
            alert(e.message || 'Failed to assign Track Catalogue #');
          } finally {
            setAssigning((p) => ({ ...p, [`${r._id}_trackCatalog`]: false }));
          }
        }}
        style={{
          padding: '6px 10px',
          background: assigning[`${r._id}_trackCatalog`] ? '#555' : '#333',
          color: '#fff',
          border: 'none',
          borderRadius: 6,
          cursor: assigning[`${r._id}_trackCatalog`] ? 'not-allowed' : 'pointer',
          fontWeight: 600,
        }}
        title="Assign from Sheet"
      >
        {assigning[`${r._id}_trackCatalog`] ? 'Assigning…' : 'Assign number'}
      </button>
    )}
  </div>
</Row>
    </>
  ) : null}

  <Row label="ISRC" compact>
  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
    <input
      value={st.isrc || ''}
      onChange={(e) => patchRelease(r._id, { isrc: e.target.value })}
      placeholder="e.g. DEWO71700870"
      style={input.sm}
      readOnly={false}
    />
    {!guest && !st.isrc && (
      <button
        disabled={assigning[`${r._id}_isrc`]}
        onClick={async () => {
          setAssigning((p) => ({ ...p, [`${r._id}_isrc`]: true }));
          try {
            const resp = await api('/api/identifiers/assign', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ releaseId: r._id, kind: 'isrc' }),
            });
            if (!resp.ok) throw new Error(await resp.text());
            const { value } = await resp.json();
            patchRelease(r._id, { isrc: value });
            await saveOne(r._id);
            alert('ISRC assigned ✅');
          } catch (e) {
            alert(e.message || 'Failed to assign ISRC');
          } finally {
            setAssigning((p) => ({ ...p, [`${r._id}_isrc`]: false }));
          }
        }}
        style={{
          padding: '6px 10px',
          background: assigning[`${r._id}_isrc`] ? '#555' : '#333',
          color: '#fff',
          border: 'none',
          borderRadius: 6,
          cursor: assigning[`${r._id}_isrc`] ? 'not-allowed' : 'pointer',
          fontWeight: 600,
        }}
        title="Assign from Sheet"
      >
        {assigning[`${r._id}_isrc`] ? 'Assigning…' : 'Assign number'}
      </button>
    )}
  </div>
</Row>

  {isSingle ? (
    <>
      <Row label="Track UPC" compact>
  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
    <input
      value={st.trackUpc || ''}
      onChange={(e) => patchRelease(r._id, { trackUpc: e.target.value })}
      placeholder="e.g. 4260571127742"
      style={input.sm}
      readOnly={false}
    />
    {!guest && !st.trackUpc && (
      <button
        disabled={assigning[`${r._id}_trackUpc`]}
        onClick={async () => {
          setAssigning((p) => ({ ...p, [`${r._id}_trackUpc`]: true }));
          try {
            const resp = await api('/api/identifiers/assign', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ releaseId: r._id, kind: 'trackUpc' }),
            });
            if (!resp.ok) throw new Error(await resp.text());
            const { value } = await resp.json();
            patchRelease(r._id, { trackUpc: value });
            await saveOne(r._id);
            alert('Track UPC assigned ✅');
          } catch (e) {
            alert(e.message || 'Failed to assign Track UPC');
          } finally {
            setAssigning((p) => ({ ...p, [`${r._id}_trackUpc`]: false }));
          }
        }}
        style={{
          padding: '6px 10px',
          background: assigning[`${r._id}_trackUpc`] ? '#555' : '#333',
          color: '#fff',
          border: 'none',
          borderRadius: 6,
          cursor: assigning[`${r._id}_trackUpc`] ? 'not-allowed' : 'pointer',
          fontWeight: 600,
        }}
        title="Assign from Sheet"
      >
        {assigning[`${r._id}_trackUpc`] ? 'Assigning…' : 'Assign number'}
      </button>
    )}
  </div>
</Row>
    </>
  ) : null}

                      <Row label="Assigned Project Catalogue #" compact>
                        <input value={projCatalog || ''} readOnly style={{ ...input.sm, opacity: 0.7 }} />
                      </Row>
                      <Row label="Assigned Project UPC" compact>
                        <input value={projUpc || ''} readOnly style={{ ...input.sm, opacity: 0.7 }} />
                      </Row>

                      <Row label="" compact>
                        <div>
                          <button
                            onClick={() => saveOne(r._id)}
                            style={{
                              padding: '8px 12px',
                              background: '#28a745',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 6,
                              cursor: 'pointer',
                              fontWeight: 700,
                            }}
                          >
                            Assign
                          </button>
                        </div>
                      </Row>
                    </>
                  )}
                </div>
              )}
              

              {/* Asset form drawer (split component) — includes per-release SAVE */}
<div style={{ marginTop: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
  {/* Left: Submit / Status button */}
  {(() => {
    const submitted = !!r.isSubmitted;
    const bg = submitted ? '#28a745' : '#f39c12'; // green when submitted, orange otherwise
    const label = submitted
      ? 'Assets submitted'
      : (openDetails[r._id] ? 'Hide Asset Form' : 'Submit Release Assets');

    return (
      <button
        onClick={() => toggleDetails(r._id)}
        style={{
          padding: '10px 16px',
          background: bg,
          color: '#fff',
          border: 'none',
          borderRadius: 8,
          cursor: 'pointer',
          fontWeight: 700,
        }}
      >
        {label}
      </button>
    );
  })()}

  {/* Right: CSV + Sheet buttons */}
  
  <div style={{ display: 'flex', gap: 8, marginLeft: 'auto', alignItems: 'center' }}>
  {!guest && (
    <>
{/* Lock / Unlock toggle (icon) */}
<button
  onClick={async () => {
    try {
      const nextSubmitted = !r.isSubmitted;
      const resp = await api(`/api/releases/${r._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          isSubmitted: nextSubmitted,
          submittedAt: nextSubmitted ? r.submittedAt || new Date().toISOString() : null,
        }),
      });
      if (!resp.ok) {
        const t = await resp.text();
        throw new Error(`${nextSubmitted ? 'Lock' : 'Unlock'} failed (${resp.status}): ${t}`);
      }

      setReleases(prev =>
        prev.map(x =>
          String(x._id) === String(r._id) ? { ...x, isSubmitted: nextSubmitted } : x
        )
      );
      setReleaseState(prev => ({
        ...prev,
        [r._id]: { ...(prev[r._id] || {}), isSubmitted: nextSubmitted },
      }));

      
      

      alert(nextSubmitted ? 'Form locked.' : 'Form unlocked. You can edit and re-submit.');
    } catch (e) {
      alert(e.message || 'Toggle failed');
    }
  }}
  title={r.isSubmitted ? 'Unlock this form to edit again' : 'Lock this form to prevent edits'}
  style={{
    background: 'transparent',
    border: 'none',
    padding: 0,
    marginRight: 6,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  }}
>
  {r.isSubmitted ? (
    <LockedIcon style={{ width: 20, height: 20, fill: '#2a2a2a' }} />
  ) : (
    <UnlockedIcon style={{ width: 20, height: 20, fill: '#2a2a2a' }} />
  )}
</button>
      {/* Download All (.zip) */}
      <button
        onClick={() =>
  window.open(
    `${API_BASE}/api/download/releases/${r._id}/all`,
    '_blank',
    'noopener,noreferrer'
  )
}
        style={{
          padding: '8px 12px',
          background: '#333',
          color: '#fff',
          border: 'none',
          borderRadius: 6,
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
        }}
        title="Download all assets for this release as a .zip"
      >
        <DownloadIcon style={{ width: 16, height: 16, fill: '#aaa' }} />
        Download All (.zip)
      </button>

{/* --- HIDE CSV BUTTON (temporarily disabled) ---
<button
  onClick={() => handleExportCsv(r._id, false)}
  style={{
    padding: '8px 12px',
    background: '#333',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
  }}
  title="Download a CSV row for this release"
>
  Download CSV
</button>
--- END HIDE --- */}

      {/* Add to Sheet */}
      <button
        onClick={() => handleAppendToSheet(r._id, r.isAppendedToSheet)}
        style={{
          padding: '8px 12px',
          background: '#444',
          color: '#fff',
          border: 'none',
          borderRadius: 6,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
        title="Append this release as a new row in the Google Sheet"
      >
        {r.isAppendedToSheet && (
          <CheckCircleIcon className="ck-check" style={{ width: 16, height: 16 }} />
        )}
        {r.isAppendedToSheet ? 'Added to Sheet' : 'Add to Sheet'}
      </button>
      {/* Add to District (single release) */}
{(() => {
  const isAdding = !!relDistrictAppending[r._id];
  const wasDistrictAdded = !!r.isAppendedToDistrict || !!relDistrictAdded[r._id];

  return (
    <>
      <button
        onClick={() => handleAppendReleaseToDistrict(r._id)}
        disabled={isAdding}
        style={{
          padding: '8px 12px',
          background: isAdding ? '#555' : '#444',
          color: '#fff',
          border: 'none',
          borderRadius: 6,
          cursor: isAdding ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
        title='Add this release to the "Automatic District" tab'
      >
        {wasDistrictAdded && (
          <CheckCircleIcon className="ck-check" style={{ width: 16, height: 16 }} />
        )}
        {wasDistrictAdded ? 'Added to District' : (isAdding ? 'Adding…' : 'Add to District')}
      </button>

      {/* Add to Killphonic (single release) */}
{(() => {
  const isAdding = !!relKillphonicAppending[r._id];
  const wasAdded = !!r.isAppendedToKillphonic || !!relKillphonicAdded[r._id];

  return (
    <button
      onClick={() => handleAppendReleaseToKillphonic(r._id)}
      disabled={isAdding || wasAdded}
      style={{
        padding: '8px 12px',
        background: isAdding || wasAdded ? '#555' : '#444',
        color: '#fff',
        border: 'none',
        borderRadius: 6,
        cursor: isAdding || wasAdded ? 'not-allowed' : 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        opacity: isAdding ? 0.8 : 1,
      }}
      title='Add this release to the "Automatic Killphonic" tab'
    >
      {wasAdded && (
        <CheckCircleIcon className="ck-check" style={{ width: 16, height: 16 }} />
      )}
      {wasAdded ? 'Added to Killphonic' : (isAdding ? 'Adding…' : 'Add to Killphonic')}
    </button>
  );
})()}

      {/* Add to Creators (single release) */}
      {(() => {
        const isAdding = !!relCreatorsAppending[r._id];
        const wasAdded = !!r.isAppendedToCreators || !!relCreatorsAdded[r._id];
        return (
          <button
            onClick={() => handleAppendReleaseToCreators(r._id)}
            disabled={isAdding}
            style={{
              padding: '8px 12px',
              background: isAdding ? '#555' : (wasAdded ? '#3a3a3a' : '#444'),
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              cursor: isAdding ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              opacity: isAdding ? 0.8 : 1,
            }}
            title="Send this release to Creators"
          >
            {wasAdded && (
              <CheckCircleIcon className="ck-check" style={{ width: 16, height: 16 }} />
            )}
            {wasAdded ? 'Added to Creators' : (isAdding ? 'Adding…' : 'Add to Creators')}
          </button>
        );
      })()}
    </>
  );
})()}
    </>
  )}
</div>
  
</div>




              {openDetails[r._id] && (
                <ReleaseAssets
  Row={Row}
  Chip={Chip}
  input={input}
  state={st}
  allArtists={allArtists}
  setField={(field, val) => patchRelease(r._id, { [field]: val })}
  onWriterChange={(idx, field, val) => onWriterChange(r._id, idx, field, val)}
  addWriter={() => addWriter(r._id)}
  removeWriter={(idx) => removeWriter(r._id, idx)}
  setPendingFile={(key, file) =>
    patchRelease(r._id, {
      pendingFiles: { ...(st.pendingFiles || {}), [key]: file },
    })
  }
  doUpload={(key, onProgress, fileOverride) => {
    const file = fileOverride || (st.pendingFiles || {})[key];
    if (!file) return alert('Choose a file first.');
    return uploadFile(file, r._id, key, onProgress);
  }}
  autoUploadFile={(label, file, onProgress) => uploadFile(file, r._id, label, onProgress)}
  releaseId={r._id}
  onViewFile={(label) => handleViewFile(r._id, label)}
  onDownloadFile={(label) => handleDownloadFile(r._id, label)}
  onSave={() => saveOne(r._id)}
  projectType={projType}
  guest={guest}
  locked={!!r.isSubmitted}
  canSubmit={isFormComplete(st, projType)}
  submitting={!!submitting[r._id]}
  onSubmit={() => submitRelease(r._id)}
/>

              )}
            </div>
          );
        })}
      </Section>
    </div>
  );
}